/**
 * @fileoverview "Shared with me": every private page and site somebody has
 * shared with this reader, newest first.
 *
 * THE LIST COSTS NOTHING TO DRAW AND EACH ROW COSTS THE SIGNER TO OPEN. A share
 * event's plaintext tags name the recipient and nothing else (see
 * `shareCollection.ts`), so before decrypting all a row can say is who sent
 * something and when. What was shared, its kind and its title are sealed inside
 * the payload. Opening one row is two NIP-44 decryptions by the reader's signer
 * (the payload, then the key envelope inside it), and a remote signer may put a
 * prompt on the reader's phone for each. So rows are opened when the reader
 * asks, one at a time or all at once, exactly as `/mine` does with private
 * titles. A headless caller with a local or role signer just opens them all.
 *
 * The title after that is free: the shared key opens the page's sealed tags
 * locally, with no further signer call.
 *
 * A SHARE WHOSE PAGE IS GONE SAYS SO. The relay answering nothing for the
 * address is what a removed page looks like, and the row says "removed" rather
 * than failing, because the share event outlives the page it pointed at.
 *
 * NOT LISTED: public pages, which are not shared with anyone, and shares the
 * reader sent to themselves, which are their own pages and live in `/mine`.
 */

import type { SignerInterface } from '@cloistr/auth/core';
import { nip19, type Event } from 'nostr-tools';

import { parseCollectionEvent } from './collectionEvent';
import { parseIndexEvent } from './indexEvent';
import { PAGE_COLLECTION_KIND, PAGE_INDEX_KIND, SHARE_KIND } from './kinds';
import { isPrivatePageEvent, openSealedPage } from './privatePage';
import { openShareEvent } from './shareCollection';

import { fetchAll, fetchNewest, relaysFor } from '@/services/nostr/relays';

/** Enough for anyone's inbox of shares; the relay caps a query anyway. */
const SHARE_LIMIT = 500;

/** NIP-01 metadata, read only for a sender's display name. */
const PROFILE_KIND = 0;

export interface SharedRow {
  /** The share event's id, which is the row's identity before it is opened. */
  id: string;
  from: string;
  sharedAt: number;
  /** Kept so the row can be opened later without asking the relay again. */
  event: Event;
}

export type OpenedShare =
  | {
      status: 'open';
      kind: number;
      coordinate: string;
      naddr: string;
      /** Null when the shared key does not open the page's sealed tags. */
      title: string | null;
    }
  | { status: 'removed'; kind: number; coordinate: string }
  /** Not a share we can read: another app's shape, or a key that does not match. */
  | { status: 'unreadable' };

/**
 * Every share event addressed to this reader, newest first, without decrypting
 * any of them.
 */
export async function listShared(pubkey: string, hints: readonly string[] = []): Promise<SharedRow[]> {
  const events = await fetchAll(relaysFor(hints), {
    kinds: [SHARE_KIND],
    '#p': [pubkey],
    limit: SHARE_LIMIT,
  });

  const seen = new Set<string>();
  const rows: SharedRow[] = [];
  for (const event of events) {
    // The relay was asked for shares addressed to this reader. Hold it to that.
    if (event.kind !== SHARE_KIND || seen.has(event.id)) continue;
    if (!event.tags.some((t) => t[0] === 'p' && t[1] === pubkey)) continue;
    if (event.pubkey === pubkey) continue;
    seen.add(event.id);
    rows.push({ id: event.id, from: event.pubkey, sharedAt: event.created_at, event });
  }
  rows.sort((a, b) => b.sharedAt - a.sharedAt);
  return rows;
}

/**
 * Open one share: decrypt it, find what it points at, and read its title.
 *
 * NEVER THROWS. Whatever goes wrong lands on `unreadable` or `removed`, because
 * a stranger can address a malformed share to anyone and that must not break
 * the reader's list.
 */
export async function openShared(
  row: SharedRow,
  signer: SignerInterface,
  hints: readonly string[] = [],
): Promise<OpenedShare> {
  const share = await openShareEvent(row.event, signer);
  if (!share) return { status: 'unreadable' };

  const target = parseCoordinate(share.address);
  if (!target || (target.kind !== PAGE_INDEX_KIND && target.kind !== PAGE_COLLECTION_KIND)) {
    return { status: 'unreadable' };
  }
  // Only the author of a thing can hand out its key. A share naming somebody
  // else's page would point the reader at a page under one byline with a key
  // vouched for by another key entirely.
  if (target.pubkey !== share.from) return { status: 'unreadable' };

  let event: Event | null = null;
  try {
    event = await fetchNewest(relaysFor(hints), {
      kinds: [target.kind],
      authors: [target.pubkey],
      '#d': [target.identifier],
    });
  } catch {
    event = null;
  }
  // The address is authoritative rather than the relay's answer, as everywhere.
  if (
    event &&
    (event.kind !== target.kind ||
      event.pubkey !== target.pubkey ||
      !event.tags.some((t) => t[0] === 'd' && t[1] === target.identifier))
  ) {
    event = null;
  }
  if (!event) return { status: 'removed', kind: target.kind, coordinate: share.address };

  return {
    status: 'open',
    kind: target.kind,
    coordinate: share.address,
    naddr: nip19.naddrEncode(target),
    title: titleWith(event, share.key),
  };
}

function titleWith(event: Event, key: Parameters<typeof openSealedPage>[1]): string | null {
  try {
    if (event.kind === PAGE_COLLECTION_KIND) return parseCollectionEvent(event, key)?.title ?? null;
    if (!isPrivatePageEvent(event)) return parseIndexEvent(event)?.title ?? null;
    const plain = openSealedPage(event, key);
    return plain ? (parseIndexEvent(plain)?.title ?? null) : null;
  } catch {
    return null;
  }
}

function parseCoordinate(coordinate: string): { kind: number; pubkey: string; identifier: string } | null {
  const first = coordinate.indexOf(':');
  const second = coordinate.indexOf(':', first + 1);
  if (first < 1 || second < 0) return null;
  const kind = Number(coordinate.slice(0, first));
  const pubkey = coordinate.slice(first + 1, second);
  const identifier = coordinate.slice(second + 1);
  if (!Number.isInteger(kind) || !/^[0-9a-f]{64}$/.test(pubkey)) return null;
  return { kind, pubkey, identifier };
}

/**
 * Display names for the people who shared something, from their own profiles.
 *
 * A sender with no profile, or one that does not parse, is simply absent from
 * the map and the caller falls back to a short npub. One query for everyone.
 */
export async function senderNames(
  pubkeys: readonly string[],
  hints: readonly string[] = [],
): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  const unique = [...new Set(pubkeys)];
  if (unique.length === 0) return names;

  const events = await fetchAll(relaysFor(hints), { kinds: [PROFILE_KIND], authors: unique });
  const newest = new Map<string, Event>();
  for (const e of events) {
    if (e.kind !== PROFILE_KIND || !unique.includes(e.pubkey)) continue;
    const held = newest.get(e.pubkey);
    if (!held || e.created_at > held.created_at) newest.set(e.pubkey, e);
  }
  for (const [pubkey, e] of newest) {
    try {
      const profile = JSON.parse(e.content) as { display_name?: unknown; name?: unknown };
      const name = [profile.display_name, profile.name].find(
        (n): n is string => typeof n === 'string' && n.trim().length > 0,
      );
      // Bounded, because a profile is a stranger's text drawn into our screen.
      if (name) names.set(pubkey, name.trim().slice(0, 64));
    } catch {
      /* not JSON */
    }
  }
  return names;
}

/** `npub1abcd…wxyz`, for a sender with no usable profile name. */
export function shortNpub(pubkey: string): string {
  const npub = nip19.npubEncode(pubkey);
  return `${npub.slice(0, 10)}…${npub.slice(-4)}`;
}
